import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../hooks/useLocale';
import apiClient from '../api/client';

interface User {
  id: number;
  google_id: string;
  email: string;
  name: string;
  avatar: string;
  is_paid: number;
  subscription_plan: string | null;
  subscription_end: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export default function Dashboard() {
  const { user, isAdmin, logout, fetchUser } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/users');
      setUsers(res.data.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin, loadUsers]);

  async function handleGrant(userId: number) {
    const days = prompt(t('days'), '30');
    if (!days) return;
    try {
      await apiClient.post('/admin/grant', {
        user_id: userId,
        days: parseInt(days, 10),
      });
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to grant access');
    }
  }

  async function handleRevoke(userId: number) {
    if (!confirm(t('revoke_access'))) return;
    try {
      await apiClient.post('/admin/revoke', { user_id: userId });
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to revoke access');
    }
  }

  function handleLogout() {
    logout();
  }

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'ru', label: 'Русский' },
    { code: 'de', label: 'Deutsch' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-xl font-bold text-primary-600">
            <Link to="/" aria-label={t('app_name')} className="hover:opacity-80 transition-opacity">
              {t('app_name')}
            </Link>
          </h1>
          <div className="flex items-center gap-4">
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value as typeof locale)}
              className="text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-red-500"
            >
              {t('logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center gap-4">
            {user?.avatar && (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            )}
            <div>
              <h2 className="text-xl font-semibold">
                {user?.name || 'User'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {user?.email}
              </p>
            </div>
            <div className="ml-auto">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  user?.is_paid
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {user?.is_paid
                  ? t('subscription_active')
                  : t('subscription_inactive')}
              </span>
            </div>
          </div>

          {user?.subscription_end && user.is_paid ? (
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              {t('subscription_ends')}:{' '}
              {new Date(user.subscription_end).toLocaleDateString()}
            </p>
          ) : (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm text-gray-500">
                10 min/day free · $5/month unlimited
              </span>
              <button
                onClick={async () => {
                  try {
                    const res = await apiClient.get('/payment/checkout');
                    const url = res.data.data.url;
                    if (url) {
                      window.open(url, '_blank');
                    } else {
                      alert('Payment not configured yet. Please run: python scripts/setup.py');
                    }
                  } catch (err: any) {
                    alert(
                      err.response?.data?.error || 'Failed to get payment link',
                    );
                  }
                }}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                Subscribe $5/month
              </button>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('admin_panel')}</h3>
              <input
                type="text"
                placeholder={t('search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-transparent"
              />
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500">
                {t('loading')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-3 pr-4">{t('name')}</th>
                      <th className="pb-3 pr-4">{t('email')}</th>
                      <th className="pb-3 pr-4">{t('status')}</th>
                      <th className="pb-3 pr-4">{t('expires')}</th>
                      <th className="pb-3 pr-4">{t('created_at')}</th>
                      <th className="pb-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr
                        key={u.id}
                        className="border-b border-gray-100 dark:border-gray-700"
                      >
                        <td className="py-3 pr-4">{u.name}</td>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                          {u.email}
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              u.is_paid
                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}
                          >
                            {u.is_paid
                              ? t('subscription_active')
                              : t('subscription_inactive')}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-xs text-gray-500">
                          {u.subscription_end
                            ? new Date(
                                u.subscription_end,
                              ).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="py-3 pr-4 text-xs text-gray-500">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleGrant(u.id)}
                              className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition"
                            >
                              {t('grant_access')}
                            </button>
                            <button
                              onClick={() => handleRevoke(u.id)}
                              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition"
                            >
                              {t('revoke_access')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
